import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// 영어 서수 변환 함수
export const getOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  const index = (v - 20) % 10;
  return n + (s[index] || s[v] || s[0]);
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources: {
      en: {
        translation: {
          root: {
            timezoneWarning: 'The time displayed is local time.',
          },
          passwordPolicy: {
            rule: 'Mix 3 or more of uppercase, lowercase, numbers and symbols for 8+ characters — or 2 of them for 10+ characters.',
            hintStart: 'Mix letters, numbers and symbols',
            hintNeedTypes: 'Use at least 2 of letters, numbers and symbols',
            hintNeedLength: '{{count}} more character(s) to go',
            hintAddTypeShortcut: 'Add {{type}} and 8 characters will be enough',
            typeUppercase: 'an uppercase letter',
            typeLowercase: 'a lowercase letter',
            typeNumber: 'a number',
            typeSymbol: 'a symbol',
            hintReady: 'You can use this password',
            match: 'Passwords match',
            mismatch: "Passwords don't match",
            showPassword: 'Show password',
            hidePassword: 'Hide password',
            violationMessage: 'Your password does not meet the requirements.',
          },
          accountLock: {
            loginFailed:
              'Please check your email address and password. Too many failed attempts will lock the account temporarily.',
            tooManyRequests:
              'Too many attempts. Please wait about a minute and try again.',
            serverError:
              'Something went wrong on our side. Please try again in a moment.',
            lockedTitle: 'Your account is locked',
            lockedDescription:
              'Too many incorrect password attempts. You can wait {{minutes}} minutes and sign in as usual, or unlock it now with the code we emailed to {{email}}.',
            waitHint:
              'If you know your password and these attempts were not yours, there is nothing to do.',
            unlockedDescription:
              'The lock has lifted. You can sign in with your usual password.',
            unlockedHint: 'You can sign in now.',
            codeLabel: 'Verification code',
            codePlaceholder: '{{length}}-digit code',
            codeHint: 'The code is valid for {{minutes}} minutes.',
            verifyButton: 'Continue',
            resendButton: 'Send the code again',
            resendDone: 'We sent a new code. Please check your email.',
            setPasswordTitle: 'Set a new password',
            setPasswordDescription:
              'Your account unlocks as soon as you set a new password.',
            unlockDone: 'Your account is unlocked. Please sign in.',
            backToLogin: 'Back to sign in',
            genericError: 'Something went wrong. Please try again.',
          },
          passwordChange: {
            advisedBanner:
              'You need to change your password in {{days}} day(s).',
            advisedBannerToday: 'You need to change your password today.',
            changeNow: 'Change now',
            dismissNotice: 'Dismiss',
            dueTitle: "It's time to change your password",
            dueDescription:
              'Passwords are changed every {{cycleDays}} days to keep your account safe. You can postpone this until {{forcedDate}}.',
            later: 'Change later',
            forcedTitle: 'Please change your password to continue',
            forcedDescription:
              'It has been more than {{cycleDays}} days since your last password change, and the grace period has ended. Set a new password to keep using the service.',
            successMessage: 'Your password has been changed.',
            continueButton: 'Continue',
            logout: 'Sign in with a different account',
          },
          supportInquiry: {
            launcherLabel: 'Contact support',
            title: 'Contact support',
            subTitle: 'We usually reply within one business day',
            name: 'Name',
            namePlaceholder: 'Your name',
            email: 'Email',
            emailPlaceholder: 'Where we should reply',
            answerTo: 'We will reply to {{email}}',
            subject: 'Subject',
            subjectPlaceholder: 'Briefly describe your issue',
            message: 'Message',
            messagePlaceholder:
              'Tell us what happened in as much detail as you can',
            submit: 'Send',
            submitting: 'Sending...',
            cancel: 'Cancel',
            successTitle: 'Your inquiry has been sent',
            successDescription:
              'We will reply to the email address you provided. Thank you.',
            close: 'Close',
            errorMessage: 'Failed to send. Please try again in a moment.',
            tooManyRequests:
              'Too many inquiries have been sent. Please try again in a minute.',
          },
          auth: {
            login: {
              upPeopleDescription: 'My personal mental coach at work',
              errorMessage:
                'Please check your email address and password again',
              forgotPassword: 'Forgot password?',
              processing: 'Logging in...',
              submitButtonText: 'Login',
              emailPlaceholder: 'Email (Work E-mail)',
              passwordPlaceholder: 'Password (PW)',
              showPassword: 'Show password',
              hidePassword: 'Hide password',
            },
            logout: {
              title: 'Logged out',
              description: 'Click the button below to log in again.',
              loginButtonText: 'Go to login page',
            },
            sendPasswordResetEmail: {
              upPeopleDescription: 'My personal mental coach at work',
              title: 'Reset password',
              description:
                'Please enter the email address you used to sign up,<br/>we will send you a link to reset your password',
              placeholder: 'Email (Work E-mail)',
              processing: 'Sending password reset email...',
              submitButtonText: 'Send',
              backToLogin: 'Back to login page',
              emailSent: 'Email sent',
              emailSentDescription:
                'We have sent a link to reset your password to the email you entered.',
              emailSentDescription2:
                'Please check your email and reset your password.',
              emailRequired: 'Please enter your email',
              errorMessage:
                'Password reset email sending failed. Please try again.',
            },
            resetPasswordFromLink: {
              title: 'Reset password for {{userName}}',
              description: 'Reset password for your UpPeople account',
              passwordRequirementsTitle: 'Password Requirements',
              passwordRequirementsMinimumLength: 'Minimum 8 characters',
              passwordRequirementsLetterAndNumber:
                'Letters and numbers combination',
              passwordMatchText: 'Passwords match',
              invalidLinkErrorMessage:
                'Invalid password reset link. Please contact the administrator.',
              passwordRequirementsErrorMessage:
                'Password must be at least 8 characters long and contain both letters and numbers',
              passwordMatchErrorMessage: 'Passwords do not match',
              errorMessage: 'Password setup failed. Please try again.',
              checkingCode: 'Checking code...',
              backToLogin: 'Back to login page',
              successMessage: 'Password reset successfully!',
              redirectingToMainPage: 'Redirecting to main page...',
              newPasswordPlaceholder: 'New password',
              confirmPasswordPlaceholder: 'Confirm new password',
              submitButtonText: 'Reset Password',
              processing: 'Processing...',
            },
            resetPassword: {
              passwordRequirementsErrorMessage:
                'Password must be at least 8 characters long and contain both letters and numbers',
              passwordMatchErrorMessage: 'Passwords do not match',
              errorMessage: 'Password reset failed. Please try again.',
              inputPasswordText: 'Enter the password to change',
              successMessage: 'Password changed successfully!',
              redirectingToMainPage: 'Redirecting to main page...',
              passwordRequirementsTitle: 'Password Requirements',
              passwordRequirementsMinimumLength: 'Minimum 8 characters',
              passwordRequirementsLetterAndNumber:
                'Letters and numbers combination',
              passwordMatchText: 'Passwords match',
              newPasswordPlaceholder: 'New password',
              confirmPasswordPlaceholder: 'Confirm new password',
              processing: 'Processing...',
              submitButtonText: 'Change Password',
            },
          },
          verifyInitialPasswordSetupCode: {
            title: 'Enter the password to change',
            guidePasswordRequirementsTitle: 'Password Requirements',
            guidePasswordRequirementsMinimumLength: 'Minimum 8 characters',
            guidePasswordRequirementsLetterAndNumber:
              'Letters and numbers combination',
            guidePasswordMatchText: 'Passwords match',
            inputCodeText: 'New password',
            inputCodeConfirmText: 'Confirm new password',
            submitButtonText: 'Change Password',
            successMessage: 'Password changed successfully',
            errorMessage: 'Password change failed. Please try again.',
            errorNotMatchPassword: 'Passwords do not match',
            invalidCodeErrorMessage:
              'Invalid setup code. Please contact the administrator.',
            codeNotFoundErrorMessage:
              'Setup code not found. Please check the link again.',
            loginPageButtonText: 'Go to login page',
            processing: 'Processing...',
            welcomeMessage: 'Welcome, {{name}}!',
            verifyingCode: 'Verifying code...',
          },
          common: {
            caseFeedback: {
              title1: 'How excited are you for the next session?',
              title2: '',
              lastSessionTitle1:
                'How satisfied were you with your coaching journey?',
              lastSessionTitle2: '',
              startDateLabel: 'Coaching Start Date',
              ratingLabel1: '1\nNot at all looking forward to it',
              ratingLabel10: '10\nVery much looking forward to it',
              ratingZoneLabel1: 'Not looking forward to it.',
              ratingZoneLabel2: 'Neutral.',
              ratingZoneLabel3: 'Looking forward to it.',
              lastSessionRatingLabel1: '1\nNot at all satisfied',
              lastSessionRatingLabel10: '10\nVery satisfied',
              lastSessionRatingZoneLabel1: 'Not satisfied.',
              lastSessionRatingZoneLabel2: 'Neutral.',
              lastSessionRatingZoneLabel3: 'Satisfied.',
              goodCommentLabel:
                'Q2-1. How excited are you for the next session?',
              badCommentLabel:
                'Q2-2. What could have been better during the session?',
              ratingText1: 'Very Dissatisfied',
              ratingText2: 'Dissatisfied',
              ratingText3: 'Neutral',
              ratingText4: 'Satisfied',
              ratingText5: 'Very Satisfied',
              good: {
                requestForComment:
                  'To help us improve our coaching, please tell us what worked well for you.',
                multipleAnswersAllowed: '(Select all that apply)',
                reason: {
                  1: 'I received practical feedback tailored to my situation.',
                  2: 'I was listened to attentively and with empathy throughout.',
                  3: 'It helped me organize my thinking and find my own answers.',
                  4: 'Clear, actionable next steps became evident.',
                  5: 'I felt comfortable and safe to speak openly.',
                  6: 'Other (please specify)',
                },
              },
              bad: {
                requestForComment:
                  'To help us improve our coaching, please tell us what felt missing or could have been better.',
                multipleAnswersAllowed: '(Select all that apply)',
                reason: {
                  1: 'The coaching lacked clear direction or structure.',
                  2: 'There was little or no meaningful progress on the issues we worked on.',
                  3: 'The coach had limited understanding of me and my work.',
                  4: 'It did not match the leadership coaching I expected.',
                  5: 'I found it difficult to speak comfortably and openly with the coach.',
                  6: 'Other (please specify)',
                },
              },
              placeholderForComment:
                'Share any thoughts from your session. (minimum 5 characters)',
              commentLength: 'Please enter at least 5 characters.',
              submitButtonText: 'Submit',
              successMessage: 'Thank you for your feedback!',
              errorMessage: 'Error submitting feedback.',
              ratingSavedLabel: 'Score saved',
              reportRatingZoneLabel1: 'Disappointing.',
              reportRatingZoneLabel2: 'Neutral.',
              reportRatingZoneLabel3: 'Satisfied.',
            },
          },
          docs: {
            pdf: {
              download: 'Download PDF',
              downloading: 'Preparing PDF…',
              failed: 'Could not create the PDF. Please try again.',
              forCase: 'PDF for coachee',
              forCaseDescription:
                'Coaching results only (1 slide). Use this when sending to the coachee.',
              forCaseHint: "Send the 'PDF for coachee' to the coachee.",
              all: 'Full PDF',
              allDescription:
                'Includes communication tips for leaders and HR (2 slides). Do not send to the coachee.',
            },
            coachingNote: {
              title: '{{sessionNum, ordinal}} Session Coaching Note',
              coachName: 'Coach {{name}}',
              mission: {
                title: 'Mission',
                myMissionLabelText: 'Mission',
                myMissionAcheivementRateLabelText: 'Progress',
              },
              summary: {
                title: 'Summary',
              },
              actionItems: {
                title: 'Action Items',
              },
            },
            coachingPlan: {
              title: "<0>{{name}}</0>'s Coaching Plan",
              fileName: '{{name}}_Coaching_Plan',
              coachName: 'Coach {{name}}',
              coreValues: {
                title: 'Core Values',
              },
              strengths: {
                title: 'Strengths',
              },
              mission: {
                title: 'Mission',
                myMissionLabelText: 'Mission',
                myMissionAcheivementRateLabelText: 'Progress',
                missionBackgroundLabelText: 'Mission Background',
              },
              caseFeedback: {
                title: 'Discovery Session Feedback',
              },
              coachingPlan: {
                title: 'Coaching Plan',
              },
            },
            coachingReport: {
              title: "<0>{{name}}</0>'s Coaching Report",
              fileName: '{{name}}_Coaching_Report',
              fileNameAll: '{{name}}_Coaching_Report_Full',
              coachName: 'Coach {{name}}',
              coreValues: {
                title: 'Core Values',
              },
              strengths: {
                title: 'Strengths',
              },
              mission: {
                title: 'Mission',
                myMissionLabelText: 'Mission',
                myMissionAcheivementRateLabelText: 'Progress',
                missionBackgroundLabelText: 'Mission Background',
              },
              caseFeedback: {
                title: 'Feedback',
              },
              coachingReview: {
                title: 'Coaching Review',
              },
            },
            missionSummary: {
              notFound: 'Report not found',
              title:
                "<span>{{name}}</span>'s Goal Achievement Progress (Overall Report)",
              mission: 'Coaching Goal',
              pre: 'Pre-Coaching',
              post: 'Post-Coaching',
              change: 'Change',
              label: 'Day {{sessionNum}}',
              legend: {
                pre: 'Before Coaching',
                mid: 'During Coaching',
                post: 'After Coaching',
              },
            },
            leadershipSummary: {
              notFound: 'Report not found',
              title:
                "<span>{{name}}</span>'s Leadership Self-Assessment (Overall Report)",
              summary: 'Summary',
              pre: 'Pre',
              post: 'Post',
              preCoaching: 'Pre-Coaching',
              postCoaching: 'Post-Coaching',
              preCoachingAvg: 'Pre-Coaching Avg',
              postCoachingAvg: 'Post-Coaching Avg',
              change: 'Change',
              point: 'pt',
              category: {
                Overall: 'Overall Average Score',
                UnderstandMyself: 'Understand Myself',
                ManageMyself: 'Manage Myself',
                UnderstandOthers: 'Understand Others',
                ManageOthers: 'Manage Others',
              },
            },
            onboardingSurvey: {
              notFound: 'Report not found',
              title: "<span>{{name}}</span>'s Onboarding Survey Result",
            },
            occupationalHealthReport: {
              notFound: 'Report not found',
              title: "<span>{{name}}</span>'s Work Health (Overall Comparison)",
              mostImprovedFactor: 'Most Improved Factor',
              averageChangeRate: 'Average Change Rate',
              몰입: 'Engagement',
              만족: 'Satisfaction',
              효능감: 'Professional Efficacy',
              pre: 'Before',
              post: 'After',
              change: 'Change',
              pt: 'pt',
            },
          },
          app: {
            recordingConsent: {
              title:
                'Terms of Service Update & STT/AI Coaching Note Feature Notice',
              body1:
                '40FY Inc. has introduced features that record and transcribe (STT) coaching sessions, and automatically generate coaching notes, summaries, and reports using artificial intelligence (AI) based on the transcribed text and related information. In connection with this, the Terms of Service have been updated as of 05/18/2026, and the updated terms can be reviewed via the button below.',
              body2:
                'If no objection is raised by the effective date (2026. 06. 18.), you will be deemed to have agreed to the updated Terms of Service. If you continue to use the service after the effective date, the updated Terms of Service will apply.',
              body3:
                'If you do not agree to the updated Terms of Service or have any inquiries, please contact us via the chat icon at the bottom right of the page or at ',
              body3After: '.',
              body4:
                'Even after the updated Terms of Service take effect, you may change whether to enable transcription and AI summary when conducting each individual session at any time.',
              toggleLabel: 'Transcription/AI Summary — On/Off anytime',
              toggleBody:
                "You can ask your coach to turn it off, or click the 'Stop taking notes' button directly in Google Meet during the session.",
              termsLink: 'Updated Terms of Service',
              privacyLink: 'Privacy Policy',
              submitButton: 'Got it',
              submittingButton: 'Processing...',
              errorMessage: 'An error occurred. Please try again.',
              termsUrl:
                'https://40fy.notion.site/Terms-of-Service-35eea339623d807ca53bc0e812fc7383',
              privacyUrl:
                'https://40fy.notion.site/Privacy-Policy-35eea339623d80d4ac5ce32cd70ced4e',
            },
            termsAgreement: {
              title: 'Terms of Service',
              description:
                'Please agree to the terms of service and privacy policy to use the service',
              allAgreeText: 'I agree to all terms',
              viewButtonText: 'View',
              termsOfServiceAgreementText:
                '(Required) Terms of Service Agreement',
              privacyPolicyAgreementText: '(Required) Privacy Policy Agreement',
              marketingAgreementText: '(Optional) Marketing Agreement',
              ageAgreementText: '(Required) 14 years or older',
              termsOfServiceAgreementLink:
                'https://40fy.notion.site/Terms-of-Service-35eea339623d807ca53bc0e812fc7383',
              privacyPolicyAgreementLink:
                'https://40fy.notion.site/Privacy-Policy-35eea339623d80d4ac5ce32cd70ced4e',
              submitButtonText: 'Submit',
              successMessage: 'Terms of Service accepted',
              errorMessage: 'Terms of Service acceptance failed',
            },
            topBar: {
              resetPassword: 'Reset PW',
              logout: 'Logout',
            },
            cycle: {
              mission: 'Mission',
              previousCycle: 'Previous Cycle',
              nextCycle: 'Next Cycle',
              coachingCycle: 'Coaching Cycle #{{number}}',
              diagnosisComplete: {
                title: 'Diagnosis complete.',
                subtitle1: "Based on {{name}}'s responses,",
                subtitle2: 'We are preparing for coach matching.',
                emailNotice:
                  'We will notify you by email when coach matching is complete.',
              },
              coachingStatus: {
                pending: {
                  title: 'Reserve the next session',
                  buttonText: 'Reserve',
                },
                coachMatched: {
                  title: 'Coach Matched',
                  buttonText: 'Reserve First Session',
                },
                completed: {
                  title: 'Coaching Completed',
                },
                reserved: {
                  title:
                    '<0>{{sessionNum, ordinal}} Session</0> is <1/> Scheduled',
                  buttonText: 'Reservation Details',
                  contactCoachButtonText: 'Contact Coach',
                },
              },
              contactCoach: {
                title: 'Contact Your Coach',
                placeholder: 'Write your message here...',
                buttonText: 'Contact',
                modalTitle: 'Send a Message to Your Coach',
                modalSubtitle:
                  'Your message will be sent to {{coachName}} coach.',
                cancelButton: 'Cancel',
                sendButton: 'Send',
                sending: 'Sending...',
                successToast: 'Your message has been sent to your coach.',
                errorToast: 'Failed to send message. Please try again.',
              },
              coachingDocs: {
                myCoachingCycle: 'My Coaching Cycle',
                coachingPlan: 'Coaching Plan',
                coachingReport: 'Coaching Report',
                view: 'View',
              },
              history: {
                coachingNote: 'Note',
                displayAll: 'Display All',
                completedCoachingHistory: 'Completed coaching history',
                coachingSession: '{{number, ordinal}} Session',
                coachingNoteNotFoundToastText: 'Coaching note not found',
              },
              todo: {
                title: 'To do',
                onboarding: 'Onboarding Survey',
                preLeadershipSelf: 'Leadership Self-Assessment',
                postLeadershipSelf: '[Post] Leadership Self-Assessment',
                waymark: 'Waymark Survey',
                postWaymark: '[Post] Job health assessment',
                peerFeedback: 'Peer Feedback',
                submitted: 'Submitted',
                viewResult: 'View Result',
                write: 'Start',
                notSubmitted: 'Not Submitted',
                completed: 'Completed',
                allCompleted: 'You have completed all the assessments.',
              },
              report: {
                title: 'Reports',
                missionSummary: '[Overall] Goal Achievement Progress',
                leadershipSummary: '[Overall] Leadership Self-Assessment',
                occupationalHealthReport: '[Overall] Job health assessment',
                peerFeedback: 'Leadership 360°',
                leadershipSelfPre: '[Pre] Leadership Self-Assessment',
                leadershipSelfPost: '[Post] Leadership Self-Assessment',
                waymark: '[Pre] Waymark Survey',
                // postWaymark: '[Post] Job health assessment',
                onboarding: 'Onboarding Survey Response',
                submittedAt: 'Submitted at',
                viewResult: 'View',
              },
              coachProfile: {
                name: 'Coach {{name}}',
                certificate: 'Certification',
                career: 'Career',
                education: 'Education',
              },
              reservation: {
                first: {
                  buttonText: 'Reserve',
                },
                calendarSheet: {
                  title: 'Select Date and Time',
                  time: 'Time',
                  timeValue: '50 minutes',
                  // 회기 구성으로 1시간이 아닌 회기의 안내. 기본(1시간 예약) 회기는 timeValue(50분)를 쓴다.
                  timeValueHours: '{{hours}} hours',
                  timeValueHoursMinutes: '{{hours}} h {{minutes}} min',
                  timeValueMinutes: '{{minutes}} minutes',
                  method: 'Method',
                  methodValue: 'Online 1:1',
                  methodValueOffline: 'In person 1:1',
                  reservingButtonText: 'Reserving...',
                  confirmButtonText: 'Confirm',
                  errorMessage: 'Reservation error',
                  successMessage: 'Reservation completed',
                  noSlotsMessage:
                    'No available slots this month. Check the next month or ask your coach to open a slot.',
                },
                timeSelector: {
                  morning: 'AM',
                  afternoon: 'PM',
                  noAvailableTimeText: 'No available time for this date',
                },
                details: {
                  title:
                    '<0>{{sessionNum, ordinal}} Session</0> Reservation Details',
                  date: 'Date',
                  meetingUrl: 'Meeting URL',
                  deliveryMode: 'Format',
                  offline:
                    'In person · at the place you agreed on with your coach',
                  coachName: 'Coach {{name}}',
                  moreInfoText: 'More Info',
                  confirmButtonText: 'Confirm',
                  alreadyCompletedErrorMessage:
                    'This reservation has already been completed',
                  reservationTodayErrorMessage:
                    'Reservation cannot be changed on the day of the reservation',
                  changeReservationButtonText: 'Change Reservation',
                  mustRead: {
                    title: 'Must Read',
                    mustReadText: 'Please read carefully',
                    mustReadList: `
                      <li>Reservation can be changed 1 day before the reservation date</li>
                      <li>Reservation cannot be changed on the day of the reservation</li>
                      <li>If you miss your reservation, the penalty will be deducted</li>
                    `.trim(),
                  },
                  guide: {
                    title: 'Guide',
                    guideText: 'Please read carefully',
                    guideHtml: `
                      <li>Please complete the surveys before the coaching</li>
                      <li>If you want to change the reservation, please click the [Change Reservation] button in the upper right corner of the screen</li>
                    `.trim(),
                  },
                },
              },
            },
            '40survey': {
              surveyProvider: {
                loading: 'Loading survey...',
              },
              layout: {
                buttonText: {
                  next: 'Next',
                  back: 'Back',
                  submit: 'Submit',
                },
              },
              ui: {
                circleScale: {
                  stronglyDisagree: 'Strongly Disagree',
                  stronglyAgree: 'Strongly Agree',
                },
                dropDownNumberInput: {
                  select: 'Select an option',
                },
                dropDownSelectInput: {
                  select: 'Select an option',
                },
                questionItem: {
                  unsupportedInputType: 'Unsupported input type: ',
                },
                rankingInput: {
                  description:
                    'Please rank the options from 1 to {{optionsLength}}.',
                  description2:
                    '1 is the highest rank, {{optionsLength}} is the lowest rank.',
                },
                termInput: {
                  view: 'View',
                  agree: 'Agree',
                  agreed: 'Agreed',
                },
                textInput: {
                  placeholder: 'Enter your answer',
                },
                yearMonthInput: {
                  selectYear: 'Select Year(s)',
                  selectMonth: 'Select Month(s)',
                  year: 'Year',
                  month: 'Month',
                },
              },
              page: {
                submitting: 'Submitting...',
                pleaseWait: 'Please wait...',
                submissionComplete: 'Submission complete. Thank you!',
                backToMainPage: 'Back to main page...',
                pageNotFound: 'Page not found',
                pageNotFoundDescription:
                  'The page you requested does not exist.',
                previousPageNotCompleted:
                  'There are still unanswered required questions in the previous page. Please complete the answers in the previous page.',
              },
            },
            onboardingSurvey: {
              layout: {
                buttonText: {
                  next: 'Next',
                  back: 'Back',
                  submit: 'Submit',
                  goHome: 'Home',
                },
              },
              intro: {
                title: 'Coaching Onboarding Survey',
                description:
                  "Let's begin your <br /> journey to grow <br /> as a leader",
                questions: {
                  title: 'Questions to ask',
                  current_concerns: "What's on your mind right now?",
                  expected_changes: 'What change do you hope for?',
                  coaching_style: 'Which coaching style suits you?',
                  estimated_time: 'Takes about 5 minutes',
                },
                start_button: 'Start',
              },
              page1: {
                title: 'Choose up to  <br /> 2 coaching topics',
                topics: {
                  self_understanding: {
                    title: 'Self-awareness',
                    description:
                      'I want to understand my self and build my own leadership.',
                  },
                  emotional_control: {
                    title: 'Emotion regulation',
                    description: 'I want to stay calm and centered.',
                  },
                  stress_management: {
                    title: 'Burnout & stress care',
                    description:
                      'I want to recharge my energy and prevent burnout.',
                  },
                  communication: {
                    title: 'Communication & feedback',
                    description:
                      'I want to communicate clearly and give better feedback.',
                  },
                  growth_support: {
                    title: 'Team growth & motivation',
                    description:
                      'I want to inspire my team by linking their needs with our goals and grow together.',
                  },
                  hiring_management: {
                    title: 'Delegation & performance',
                    description:
                      'I want to improve perfomace throught trust and healthy delegation, not micromanaging.',
                  },
                  relationship: {
                    title: 'Conflict management',
                    description:
                      'I want to resolve ongoing conflicts in the team',
                  },
                  goal_achievement: {
                    title: 'Vision & strategy',
                    description:
                      "I want to organize and communicate our team's vision, goals, and the strategies to achieve them.",
                  },
                  culture_change: {
                    title: 'Culture and change management',
                    description:
                      'I want to shape a new culture and guide my team through big changes.',
                  },
                },
                errorMessage:
                  'You can select up to 2 items.\nPlease deselect an item before choosing another.',
              },
              page2: {
                title: 'What is your main goal <br /> for coaching?',
                selectedTopics: 'Selected topics',
                topicLabels: {
                  self_understanding: 'Self-awareness',
                  emotional_control: 'emotion regulation',
                  stress_management: 'burnout & stress care',
                  communication: 'communication & feedback',
                  growth_support: 'team growth & motivation',
                  hiring_management: 'delegation & performance',
                  relationship: 'conflict management',
                  goal_achievement: 'vision & strategy',
                  culture_change: 'Culture and change management',
                },
                goalDescription: 'What will I look like after coaching?',
                goalDescriptionPlaceholder:
                  'After coaching, how will you think, feel, and lead differently? Picture your future self, the clearer it is, the closer it gets.',
                goalBackground: 'What made you want to set this goal?',
                goalBackgroundPlaceholder:
                  'Feel free to share anything you’ve been struggling with or an area where you’d like to grow.',
                maxLength: '200 max',
              },
              page3: {
                title: 'How close are you  <br /> to your goal?',
                description: 'Please enter your current level of achievement.',
                description2:
                  "It's okay if you're not there yet. We will help you move closer to the future you want.",
              },
              coachIntro: {
                title: 'Leadership Coaching',
                description:
                  'Coaching is a <br/> journey to <span>unlock</span> <br/> <span>your full potential<span>',
                quote: {
                  description:
                    'The coach asked the right <br/> questions that helped me find <br/> my own answers. I left each <br/> session with real insights.',
                  by: 'Team Lead, Medical AI Company',
                },
              },
              part1: {
                title: 'Coaching Onboarding Survey <span>Part 1.</span>',
                description:
                  'Your answers will help shape your coaching journey. This survey is confidential — feel free to answer openly.',
                questions: {
                  coaching_confidence: 'I believe coaching will help my growth',
                  topic_clarity:
                    "I'm clear on the topics I want to explore <br/> through coaching",
                  timing_relevance:
                    'Now feels like the right time for me <br/> to start coaching',
                  previous_coaching_experience:
                    'Have you experienced <br/> 1:1 coaching before?',
                },
                answers: {
                  '1': 'Strongly disagree',
                  '2': 'Disagree',
                  '3': 'Neutral',
                  '4': 'Agree',
                  '5': 'Strongly agree',
                  previous_coaching_experience_yes: 'Yes',
                  previous_coaching_experience_no: 'No',
                },
              },
              coachMatching: {
                title: 'Coaching Onboarding Survey',
                description:
                  "<h2>Based on your responses, </h2><h1>we'll design the  <br /> ideal coaching journey with <br /> the right coach for you </h1>",
              },
              part2: {
                title: 'Coaching Onboarding Survey <span>Part 2.</span>',
                description:
                  'Your answers will help shape your coaching journey. This survey is confidential — feel free to answer openly.',
                preferredGuidanceStyle: {
                  question: 'In coaching, I prefer',
                  options: {
                    a: 'Finding my own way',
                    b: 'Getting clear guidance',
                  },
                },
                preferredProblemSolvingApproach: {
                  question: 'In coaching, I expect',
                  options: {
                    a: 'Understanding root causes',
                    b: 'Quick action and solutions',
                  },
                },
                preferredFeedbackStyle: {
                  question: 'I prefer feedback that is',
                  options: {
                    a: 'Thoughtful and soft',
                    b: 'Direct and clear',
                  },
                },
                preferredCoachExpertise: {
                  question: 'I want my coach to bring',
                  options: {
                    a: 'People insight',
                    b: 'Knowledge and experience',
                  },
                },
                coachRequests: {
                  question:
                    'Is there anything else <br /> you hope from your coach?',
                  placeholder: 'e.g. communication style',
                  maxLength: '150 max',
                },
                confirmModal: {
                  title: 'Are you ready to submit your answers?',
                  cancelButtonText: 'No',
                  confirmButtonText: 'Yes',
                },
                successModal: {
                  title: 'Your answers have been submitted!',
                  confirmButtonText: 'OK',
                },
              },
              result: {
                title: 'Every leader<br/>is unique.',
                description:
                  '<p>Uncover</p><p><span>your true potential</span></p><p>with UpPeople.</p>',
                nextButtonText: 'Go to home',
              },
            },
          },
          co: {
            coachingManagement: {
              confirm: 'Confirm',
              meetingUrlMissing:
                'Meeting link not found. Please try again shortly.',
              accountNotice: {
                title: 'Account Notice',
                body: 'When joining Google Meet, if your browser is signed in with an account other than your UpPeople domain account (up-people.com), you may be restricted from entering the coaching session.',
                highlight:
                  'For a smooth coaching session, please make sure to sign in (switch account) with your UpPeople domain account (up-people.com) before joining.',
                confirmButton: 'Got it',
              },
            },
          },
        },
      },
      ko: {
        translation: {
          root: {
            timezoneWarning: '화면에 표시된 시간은 현지 시각입니다.',
          },
          passwordPolicy: {
            rule: '영문 대문자·소문자·숫자·특수문자 중 3가지 이상을 섞으면 8자, 2가지만 섞으면 10자 이상',
            hintStart: '영문과 숫자, 특수문자를 섞어주세요',
            hintNeedTypes: '영문·숫자·특수문자 중 2가지 이상 섞어주세요',
            hintNeedLength: '{{count}}자만 더 입력해주세요',
            hintAddTypeShortcut:
              '{{type}}를 하나 넣으면 지금 길이로도 충분해요',
            typeUppercase: '영문 대문자',
            typeLowercase: '영문 소문자',
            typeNumber: '숫자',
            typeSymbol: '특수문자',
            hintReady: '사용할 수 있는 비밀번호예요',
            match: '비밀번호가 일치해요',
            mismatch: '비밀번호가 일치하지 않아요',
            showPassword: '비밀번호 보기',
            hidePassword: '비밀번호 숨기기',
            violationMessage: '비밀번호가 요구사항을 충족하지 않습니다.',
          },
          accountLock: {
            loginFailed:
              '이메일 주소 또는 비밀번호를 확인해주세요. 여러 번 틀리면 계정이 일시적으로 잠깁니다.',
            tooManyRequests:
              '시도가 너무 잦습니다. 1분쯤 뒤에 다시 시도해주세요.',
            serverError:
              '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
            lockedTitle: '계정이 잠겼습니다',
            lockedDescription:
              '비밀번호를 여러 번 잘못 입력했습니다. {{minutes}}분 기다렸다 평소처럼 로그인하시거나, {{email}} 로 보낸 인증번호로 지금 바로 푸실 수 있습니다.',
            waitHint:
              '비밀번호를 알고 계시고 본인이 잘못 입력한 게 아니라면, 따로 하실 일은 없습니다.',
            unlockedDescription:
              '잠금이 풀렸습니다. 평소 비밀번호로 로그인하시면 됩니다.',
            unlockedHint: '이제 로그인하실 수 있습니다.',
            codeLabel: '인증번호',
            codePlaceholder: '{{length}}자리 숫자',
            codeHint: '인증번호는 {{minutes}}분간 유효합니다.',
            verifyButton: '다음',
            resendButton: '인증번호 다시 받기',
            resendDone: '인증번호를 다시 보냈습니다. 메일을 확인해주세요.',
            setPasswordTitle: '새 비밀번호를 설정해주세요',
            setPasswordDescription:
              '비밀번호를 새로 설정하면 잠금이 바로 풀립니다.',
            unlockDone: '잠금이 해제되었습니다. 다시 로그인해주세요.',
            backToLogin: '로그인으로 돌아가기',
            genericError: '문제가 발생했습니다. 다시 시도해주세요.',
          },
          passwordChange: {
            advisedBanner: '{{days}}일 후 비밀번호를 변경해야 합니다.',
            advisedBannerToday: '오늘 비밀번호를 변경해야 합니다.',
            changeNow: '지금 변경하기',
            dismissNotice: '안내 닫기',
            dueTitle: '비밀번호를 변경할 시기입니다',
            dueDescription:
              '계정을 안전하게 지키기 위해 {{cycleDays}}일마다 비밀번호를 변경합니다. {{forcedDate}}까지 미룰 수 있습니다.',
            later: '다음에 변경하기',
            forcedTitle: '비밀번호를 변경해야 이용할 수 있습니다',
            forcedDescription:
              '마지막으로 비밀번호를 변경한 뒤 {{cycleDays}}일이 지나 유예 기간까지 끝났습니다. 새 비밀번호를 설정하면 이어서 이용할 수 있습니다.',
            successMessage: '비밀번호를 변경했습니다',
            continueButton: '계속하기',
            logout: '다른 계정으로 로그인하기',
          },
          supportInquiry: {
            launcherLabel: '문의하기',
            title: '문의하기',
            subTitle: '영업일 기준 1일 이내에 답변드립니다',
            name: '이름',
            namePlaceholder: '이름을 입력해 주세요',
            email: '이메일',
            emailPlaceholder: '답변받으실 이메일 주소',
            answerTo: '{{email}} 으로 답변드립니다',
            subject: '제목',
            subjectPlaceholder: '어떤 문제인지 간단히 적어주세요',
            message: '문의 내용',
            messagePlaceholder: '어떤 상황인지 최대한 자세히 적어주세요',
            submit: '보내기',
            submitting: '보내는 중...',
            cancel: '취소',
            successTitle: '문의가 접수되었습니다',
            successDescription:
              '입력하신 이메일 주소로 답변드리겠습니다. 감사합니다.',
            close: '닫기',
            errorMessage: '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.',
            tooManyRequests:
              '문의가 너무 많이 전송되었습니다. 1분 후 다시 시도해 주세요.',
          },
          auth: {
            login: {
              upPeopleDescription: '회사에서 만나는 나만의 멘탈코칭',
              errorMessage: '이메일 주소 및 비밀번호를 다시 확인해 주세요',
              forgotPassword: '비밀번호를 잊으셨나요?',
              processing: '로그인 중...',
              submitButtonText: '로그인',
              emailPlaceholder: '이메일(Work E-mail)',
              passwordPlaceholder: '비밀번호(PW)',
              showPassword: '비밀번호 표시',
              hidePassword: '비밀번호 숨기기',
            },
            logout: {
              title: '로그아웃 되었습니다',
              description: '다시 로그인하려면 아래를 클릭하세요.',
              loginButtonText: '로그인 페이지로 이동',
            },
            sendPasswordResetEmail: {
              upPeopleDescription: '회사에서 만나는 나만의 멘탈코칭',
              title: '비밀번호 재설정',
              description:
                '가입 시 사용한 이메일을 입력하시면 <br /> 비밀번호 재설정 링크를 발송해 드립니다.',
              placeholder: '이메일(Work E-mail)',
              processing: '전송 중...',
              submitButtonText: '재설정 이메일 보내기',
              backToLogin: '로그인 페이지로 돌아가기',
              emailSent: '이메일이 전송되었습니다',
              emailSentDescription:
                '입력하신 이메일로 비밀번호 재설정 링크를 발송했습니다.',
              emailSentDescription2:
                '이메일을 확인하여 비밀번호를 재설정해주세요.',
              emailRequired: '이메일을 입력해주세요',
              errorMessage:
                '비밀번호 재설정 이메일 전송에 실패했습니다. 다시 시도해주세요.',
            },
            resetPasswordFromLink: {
              title: '{{userName}}님의 비밀번호를 재설정해주세요',
              description: '업피플 계정의 비밀번호를 재설정해주세요',
              passwordRequirementsTitle: '비밀번호 요구사항',
              passwordRequirementsMinimumLength: '최소 8자 이상',
              passwordRequirementsLetterAndNumber: '영문, 숫자 조합',
              passwordMatchText: '비밀번호 일치',
              invalidLinkErrorMessage:
                '유효하지 않은 비밀번호 재설정 링크입니다. 관리자에게 문의하세요.',
              passwordRequirementsErrorMessage:
                '비밀번호는 8자 이상이며, 영문과 숫자를 포함해야 합니다',
              passwordMatchErrorMessage: '비밀번호가 일치하지 않습니다',
              errorMessage: '비밀번호 설정에 실패했습니다. 다시 시도해주세요.',
              checkingCode: '설정 코드 확인 중...',
              backToLogin: '로그인 페이지로 돌아가기',
              successMessage: '비밀번호가 성공적으로 설정되었습니다!',
              redirectingToMainPage: '메인 페이지로 이동합니다...',
              newPasswordPlaceholder: '새 비밀번호',
              confirmPasswordPlaceholder: '새 비밀번호 확인',
              submitButtonText: '비밀번호 설정',
              processing: '처리 중...',
            },
            resetPassword: {
              passwordRequirementsErrorMessage:
                '비밀번호는 8자 이상이며, 영문과 숫자를 포함해야 합니다',
              passwordMatchErrorMessage: '비밀번호가 일치하지 않습니다',
              errorMessage: '비밀번호 변경에 실패했습니다. 다시 시도해주세요.',
              inputPasswordText: '변경할 비밀번호를 입력해주세요',
              successMessage: '비밀번호가 성공적으로 변경되었습니다!',
              redirectingToMainPage: '메인 페이지로 이동합니다...',
              passwordRequirementsTitle: '비밀번호 요구사항',
              passwordRequirementsMinimumLength: '최소 8자 이상',
              passwordRequirementsLetterAndNumber: '영문, 숫자 조합',
              passwordMatchText: '비밀번호 일치',
              newPasswordPlaceholder: '새 비밀번호',
              confirmPasswordPlaceholder: '새 비밀번호 확인',
              processing: '처리 중...',
              submitButtonText: '비밀번호 변경',
            },
          },
          verifyInitialPasswordSetupCode: {
            title: '업피플 계정의 초기 비밀번호를 설정해주세요',
            guidePasswordRequirementsTitle: '비밀번호 요구사항',
            guidePasswordRequirementsMinimumLength: '최소 8자 이상',
            guidePasswordRequirementsLetterAndNumber: '영문, 숫자 조합',
            guidePasswordMatchText: '비밀번호 일치',
            inputCodeText: '새 비밀번호',
            inputCodeConfirmText: '새 비밀번호 확인',
            submitButtonText: '비밀번호 설정',
            successMessage: '비밀번호가 성공적으로 설정되었습니다!',
            errorMessage: '비밀번호 설정에 실패했습니다. 다시 시도해주세요.',
            errorNotMatchPassword: '비밀번호가 일치하지 않습니다.',
            invalidCodeErrorMessage:
              '유효하지 않은 설정 코드입니다. 관리자에게 문의하세요.',
            codeNotFoundErrorMessage:
              '설정 코드를 찾을 수 없습니다. 링크를 다시 확인해주세요.',
            loginPageButtonText: '로그인 페이지로 이동',
            processing: '처리 중...',
            welcomeMessage: '{{name}}님, 환영합니다!',
            verifyingCode: '설정 코드 확인 중...',
          },
          common: {
            caseFeedback: {
              title1: '다음 세션이 얼마나 기대되시나요?',
              title2: '',
              lastSessionTitle1: '함께한 코칭, 얼마나 만족스러우셨나요?',
              lastSessionTitle2: '',
              startDateLabel: '코칭 시작일',
              ratingLabel1: '1점\n전혀 기대되지 않는다',
              ratingLabel10: '10점\n매우 기대된다',
              ratingZoneLabel1: '기대되지 않습니다.',
              ratingZoneLabel2: '보통입니다.',
              ratingZoneLabel3: '기대됩니다.',
              lastSessionRatingLabel1: '1점\n전혀 만족스럽지 않다',
              lastSessionRatingLabel10: '10점\n매우 만족스럽다',
              lastSessionRatingZoneLabel1: '만족스럽지 않습니다.',
              lastSessionRatingZoneLabel2: '보통입니다.',
              lastSessionRatingZoneLabel3: '만족스럽습니다.',
              goodCommentLabel:
                'Q2-1. 세션 중 <u>좋았던 점</u>에 대해, 자유롭게 작성해주시길 바랍니다.',
              badCommentLabel:
                'Q2-2. 세션 중 <u>아쉬웠던 점</u>이 있으셨다면, 자유롭게 작성해주시길 바랍니다.',
              ratingText1: '매우 나쁨',
              ratingText2: '나쁨',
              ratingText3: '보통',
              ratingText4: '만족',
              ratingText5: '매우 만족',
              good: {
                requestForComment:
                  '더 좋은 코칭을 위해<br/>어떤 점이 좋았는지 알려주세요!',
                multipleAnswersAllowed: '(복수 선택 가능)',
                reason: {
                  1: '내 상황에 맞춘 실용적인 피드백을 받았어요',
                  2: '끝까지 내 말에 경청하고 공감해 주었어요',
                  3: '생각 정리와 스스로 답을 찾는 데 도움이 되었어요',
                  4: '실행 가능한 다음 단계(해결책)가 명확해졌어요',
                  5: '편안하고 안전하게 이야기할 수 있었어요',
                  6: '기타 (직접 입력)',
                },
              },
              bad: {
                requestForComment:
                  '더 좋은 코칭을 위해<br/>어떤 점이 아쉬웠는지 알려주세요!',
                multipleAnswersAllowed: '(복수 선택 가능)',
                reason: {
                  1: '코칭 방향성이 모호하거나, 구조화 되지 않았어요',
                  2: '문제 해결에 실질적 진전이 없었어요',
                  3: '코치께서 나와 내 업무에 대한 이해도가 낮았어요',
                  4: '기대했던 리더십 코칭과 달랐어요',
                  5: '코치와 편안하게 대화하기 어려웠어요',
                  6: '기타 (직접 입력)',
                },
              },
              placeholderForComment:
                '코칭을 받으며 느낀 점을 자유롭게 남겨 주세요. (5자 이상)',
              commentLength: '5자 이상 입력해주세요.',
              submitButtonText: '제출하기',
              successMessage: '소중한 의견 감사합니다!',
              errorMessage: '평가 결과 제출 중 오류가 발생했습니다.',
              ratingSavedLabel: '점수 저장됨',
              reportRatingZoneLabel1: '아쉬웠습니다.',
              reportRatingZoneLabel2: '보통입니다.',
              reportRatingZoneLabel3: '만족스러웠습니다.',
            },
          },
          docs: {
            pdf: {
              download: 'PDF 다운로드',
              downloading: 'PDF 만드는 중…',
              failed: 'PDF를 만들지 못했어요. 다시 시도해 주세요.',
              forCase: '대상자용 PDF',
              forCaseDescription:
                '코칭 성과 1장만 담아요. 대상자에게 보낼 때 쓰세요.',
              forCaseHint: "대상자에게는 '대상자용 PDF'를 보내세요.",
              all: '전체 PDF',
              allDescription:
                '상위리더·HR 소통 Tip까지 2장을 담아요. 대상자에게는 보내지 마세요.',
            },
            coachingNote: {
              title: '{{sessionNum}}회기 코칭 노트',
              coachName: '{{name}} 코치',
              mission: {
                title: '목표',
                myMissionLabelText: '나의 목표',
                myMissionAcheivementRateLabelText: '목표 달성도',
              },
              summary: {
                title: '코칭 요약',
              },
              actionItems: {
                title: '액션 아이템',
              },
            },
            coachingPlan: {
              title: '<0>{{name}}</0>님의 코칭 플랜',
              fileName: '{{name}}_코칭플랜',
              coachName: '{{name}} 코치',
              coreValues: {
                title: '대상자 핵심 성향',
              },
              strengths: {
                title: '대상자 핵심 강점',
              },
              mission: {
                title: '목표',
                myMissionLabelText: '나의 목표',
                myMissionAcheivementRateLabelText: '목표 달성도',
                missionBackgroundLabelText: '목표 설정 배경',
              },
              caseFeedback: {
                title: '대상자 디스커버리 세션 리뷰',
              },
              coachingPlan: {
                title: '코칭 플랜',
              },
            },
            coachingReport: {
              title: '<0>{{name}}</0>님의 코칭 리포트',
              fileName: '{{name}}_코칭리포트',
              fileNameAll: '{{name}}_코칭리포트_전체',
              coachName: '{{name}} 코치',
              coreValues: {
                title: '대상자 핵심 성향',
              },
              strengths: {
                title: '대상자 핵심 강점',
              },
              mission: {
                title: '목표',
                myMissionLabelText: '나의 목표',
                myMissionAcheivementRateLabelText: '목표 달성도',
                missionBackgroundLabelText: '목표 설정 배경',
              },
              caseFeedback: {
                title: '대상자 리뷰',
              },
              coachingReview: {
                title: '코칭 리뷰',
              },
            },
            missionSummary: {
              notFound: '보고서를 찾을 수 없습니다',
              title: '<span>{{name}}</span>님의 목표 달성도 추이 (종합 비교)',
              mission: '코칭 목표',
              pre: '코칭 전',
              post: '코칭 후',
              change: '변화도',
              label: '{{sessionNum}}회기',
              legend: {
                pre: '코칭 전',
                mid: '코칭 중',
                post: '코칭 후',
              },
            },
            leadershipSummary: {
              notFound: '보고서를 찾을 수 없습니다',
              title: '<span>{{name}}</span>님의 리더십 자가진단 (종합 비교)',
              summary: '요약',
              pre: '코칭 전',
              post: '코칭 후',
              preCoaching: '코칭 전',
              postCoaching: '코칭 후',
              preCoachingAvg: '코칭 전 평균',
              postCoachingAvg: '코칭 후 평균',
              change: '변화도',
              point: '점',
              category: {
                Overall: '총 평균 점수',
                UnderstandMyself: '자기이해',
                ManageMyself: '자기관리',
                UnderstandOthers: '타인이해',
                ManageOthers: '타인관리',
              },
            },
            onboardingSurvey: {
              notFound: '보고서를 찾을 수 없습니다',
              title: '<span>{{name}}</span>님의 온보딩 설문 응답지',
            },
            occupationalHealthReport: {
              notFound: '보고서를 찾을 수 없습니다',
              title: '<span>{{name}}</span>님의 직무건강 (종합 비교)',
              mostImprovedFactor: '가장 개선된 요인',
              averageChangeRate: '요인 평균 변화율',
              몰입: '몰입',
              만족: '만족',
              효능감: '효능감',
              pre: '코칭 전',
              post: '코칭 후',
              change: '변화도',
              pt: '점',
            },
          },
          app: {
            recordingConsent: {
              title: '이용약관 개정 및 STT/AI 코칭노트 기능 도입 안내',
              body1:
                '40FY㈜는 상담 세션의 녹음 및 전사(STT), 전사 텍스트 및 관련 정보를 기반으로 인공지능(AI)을 활용하여 코칭 내용 요약, 노트, 리포트 등의 초안을 자동으로 생성·제공하는 기능을 도입하였습니다. 이와 관련하여 2026년 05월 18일자로 서비스 이용약관이 개정되었으며, 개정 약관은 아래 버튼을 통해 확인하실 수 있습니다.',
              body2:
                '시행일(2026. 06. 18.)까지 별도의 이의 제기가 없으신 경우, 개정 이용약관에 동의하신 것으로 간주되며, 시행일 이후 서비스를 계속 이용하시는 경우 개정 이용약관이 적용됩니다.',
              body3:
                '개정 이용약관에 동의하지 않으시거나 문의 사항이 있으신 경우, 페이지 우측 하단의 채널톡 또는 ',
              body3After: '으로 문의해 주시기 바랍니다.',
              body4:
                '변경되는 약관이 적용되더라도, 추후 필요에 따라 각 세션 진행 시 전사 및 AI 요약 기능 활성화 여부는 변경하실 수 있습니다.',
              toggleLabel: '전사/AI 요약 기능, 언제든 On/Off 가능해요.',
              toggleBody:
                "코치에게 요청하거나, 세션 진행 중 Google Meet 화면에서 '회의록 작성 중지' 버튼을 직접 클릭하여 끌 수 있습니다.",
              termsLink: '개정 이용약관',
              privacyLink: '개인정보처리방침',
              submitButton: '확인했어요',
              submittingButton: '처리 중...',
              errorMessage: '처리 중 오류가 발생했습니다. 다시 시도해주세요.',
              termsUrl:
                'https://40fy.notion.site/35eea339623d802b8ad6dba1b67bac36',
              privacyUrl:
                'https://40fy.notion.site/35eea339623d805d9117c981096ccf17',
            },
            termsAgreement: {
              title: '약관 동의하기',
              description: '서비스 이용을 위해 약관에 동의해주세요',
              allAgreeText: '모두 동의합니다',
              viewButtonText: '보기',
              termsOfServiceAgreementText: '(필수) 서비스 이용약관 동의',
              privacyPolicyAgreementText: '(필수) 개인정보 수집 및 이용 동의',
              ageAgreementText: '(필수) 만 14세 이상입니다',
              marketingAgreementText: '(선택) 마케팅 동의',
              termsOfServiceAgreementLink:
                'https://40fy.notion.site/35eea339623d802b8ad6dba1b67bac36',
              privacyPolicyAgreementLink:
                'https://40fy.notion.site/35eea339623d805d9117c981096ccf17',
              submitButtonText: '동의하기',
              successMessage: '약관 동의가 완료되었습니다',
              errorMessage: '약관 동의에 실패했습니다',
            },
            topBar: {
              resetPassword: '비밀번호 변경',
              logout: '로그아웃',
            },
            cycle: {
              mission: '미션',
              previousCycle: '이전 사이클',
              nextCycle: '다음 사이클',
              coachingCycle: '코칭 사이클 #{{number}}',
              diagnosisComplete: {
                title: '진단이 완료되었습니다.',
                subtitle1: '{{name}}님의 응답을 바탕으로',
                subtitle2: '코치 매칭을 준비 중입니다.',
                emailNotice: '코치 매칭이 완료되면 이메일로 알려드립니다.',
              },
              coachingStatus: {
                pending: {
                  title: '다음 코칭 예약하기',
                  buttonText: '예약하기',
                },
                coachMatched: {
                  title: '코치가 매칭되었습니다',
                  buttonText: '첫 코칭 예약하기',
                },
                completed: {
                  title: '모든 코칭이 완료되었습니다',
                },
                reserved: {
                  title:
                    '<0>{{sessionNum}}회기</0> 코칭이 <1/>예정되어 있습니다',
                  buttonText: '예약 확인',
                  contactCoachButtonText: '코치에게 문의하기',
                },
              },
              contactCoach: {
                title: '코치에게 문의하기',
                placeholder: '문의 내용을 입력해주세요...',
                buttonText: '문의하기',
                modalTitle: '코치에게 메일 보내기',
                modalSubtitle: '{{coachName}} 코치님께 메일이 발송됩니다.',
                cancelButton: '취소',
                sendButton: '보내기',
                sending: '발송 중...',
                successToast: '코치에게 메일이 발송되었습니다.',
                errorToast: '메일 발송에 실패했습니다. 다시 시도해주세요.',
              },
              coachingDocs: {
                myCoachingCycle: '나의 코칭 사이클',
                coachingPlan: '코칭 플랜',
                coachingReport: '코칭 리포트',
                view: '열람하기',
              },
              history: {
                coachingNote: '코칭 노트',
                displayAll: '모두 보기',
                completedCoachingHistory: '완료된 코칭 내역',
                coachingSession: '{{number}}회기 코칭',
                coachingNoteNotFoundToastText: '코칭 노트가 없습니다.',
              },
              todo: {
                title: '할 일',
                onboarding: '온보딩 설문',
                preLeadershipSelf: '리더십 자가진단',
                postLeadershipSelf: '[재검사] 리더십 자가진단',
                waymark: '웨이마크 진단',
                postWaymark: '[재검사] 직무건강 진단',
                peerFeedback: '동료 피드백',
                submitted: '제출 완료',
                viewResult: '결과 보기',
                write: '작성하기',
                notSubmitted: '미제출',
                completed: '완료',
                allCompleted: '모든 설문을 완료했습니다.',
              },
              report: {
                title: '보고서',
                missionSummary: '[종합 비교] 목표 달성도 추이',
                leadershipSummary: '[종합 비교] 리더십 자가진단',
                occupationalHealthReport: '[종합 비교] 직무건강 진단',
                peerFeedback: '리더십 360도 다면진단',
                leadershipSelfPre: '[코칭 전] 리더십 자가진단',
                leadershipSelfPost: '[코칭 후] 리더십 자가진단',
                waymark: '[코칭 전] 웨이마크 진단',
                // postWaymark: '[코칭 후] 직무건강 진단',
                onboarding: '온보딩 설문 응답',
                submittedAt: '제출 완료',
                viewResult: '결과 보기',
              },
              coachProfile: {
                name: '{{name}} 코치',
                certificate: '보유 자격',
                career: '대표 이력',
                education: '학력',
              },
              reservation: {
                first: {
                  buttonText: '예약하기',
                },
                calendarSheet: {
                  title: '날짜와 시간을 선택해 주세요',
                  time: '시간',
                  timeValue: '50분',
                  // 회기 구성으로 1시간이 아닌 회기의 안내. 기본(1시간 예약) 회기는 timeValue(50분)를 쓴다.
                  timeValueHours: '{{hours}}시간',
                  timeValueHoursMinutes: '{{hours}}시간 {{minutes}}분',
                  timeValueMinutes: '{{minutes}}분',
                  method: '진행방식',
                  methodValue: '온라인 1:1',
                  methodValueOffline: '오프라인(대면) 1:1',
                  reservingButtonText: '예약 중...',
                  confirmButtonText: '예약하기',
                  errorMessage: '예약 중 오류가 발생했습니다.',
                  successMessage: '예약이 완료되었습니다.',
                  noSlotsMessage:
                    '이번 달 예약 가능한 슬롯이 없습니다. 다음 달을 확인하거나 코치님께 슬롯 오픈을 요청해 주세요.',
                },
                timeSelector: {
                  morning: '오전',
                  afternoon: '오후',
                  noAvailableTimeText: '이 날짜에 예약 가능한 시간이 없습니다.',
                },
                details: {
                  title: '<0>{{sessionNum}}회기</0> 예약 안내',
                  date: '일시',
                  meetingUrl: '주소',
                  deliveryMode: '진행 방식',
                  offline: '오프라인(대면) · 코치님과 약속하신 장소',
                  coachName: '{{name}} 코치',
                  moreInfoText: '자세히',
                  confirmButtonText: '확인',
                  alreadyCompletedErrorMessage: '이미 완료된 예약입니다.',
                  reservationTodayErrorMessage:
                    '예약 당일에는 일시 변경이 불가합니다.',
                  changeReservationButtonText: '예약 변경',
                  mustRead: {
                    title: '꼭 읽어주세요!',
                    mustReadText: '주의 사항을 꼭 읽어주세요',
                    mustReadList: `
                      <li>예약일 1일 전까지 일시 변경이 가능합니다.</li>
                      <li>예약 당일에 일시 변경은 불가합니다.</li>
                      <li>노쇼 시에 패널티 금액이 부과될 수 있습니다.</li>
                    `.trim(),
                  },
                  guide: {
                    title: '이용 가이드',
                    guideText: '주의 사항을 꼭 읽어주세요',
                    guideHtml: `
                      <li>코칭 전에 업피플 검사를 꼭 진행해 주세요.</li>
                      <li>예약 변경을 원하시면 화면 우측 상단의 [예약 변경] 버튼을 눌러주세요.</li>
                    `.trim(),
                  },
                },
              },
            },
            '40survey': {
              surveyProvider: {
                loading: '설문지를 불러오는 중입니다...',
              },
              layout: {
                buttonText: {
                  next: '다음',
                  back: '이전',
                  submit: '제출',
                },
              },
              ui: {
                circleScale: {
                  stronglyDisagree: '전혀 그렇지 않다',
                  stronglyAgree: '매우 그렇다',
                },
                dropDownNumberInput: {
                  select: '선택해주세요',
                },
                dropDownSelectInput: {
                  select: '선택해주세요',
                },
                questionItem: {
                  unsupportedInputType: '지원되지 않는 입력 유형: ',
                },
                rankingInput: {
                  description:
                    '1부터 {{optionsLength}}까지 순위를 지정해주세요.',
                  description2:
                    '1이 가장 높은 순위, {{optionsLength}}가 가장 낮은 순위를 나타냅니다.',
                },
                termInput: {
                  view: '보러가기',
                  agree: '동의합니다',
                  agreed: '동의함',
                },
                textInput: {
                  placeholder: '답변을 입력해주세요',
                },
                yearMonthInput: {
                  selectYear: '년 선택',
                  selectMonth: '개월 선택',
                  year: '년',
                  month: '개월',
                },
              },
              page: {
                submitting: '설문을 제출 중입니다',
                pleaseWait: '잠시만 기다려주세요...',
                submissionComplete: '설문 제출이 완료되었습니다. 감사합니다!',
                backToMainPage: '메인 페이지로 이동합니다...',
                pageNotFound: '페이지를 찾을 수 없습니다',
                pageNotFoundDescription: '요청하신 페이지가 존재하지 않습니다.',
                previousPageNotCompleted:
                  '이전 페이지에 아직 완료되지 않은 필수 질문이 있습니다. 이전 페이지로 돌아가서 응답을 완료해주세요.',
              },
            },
            onboardingSurvey: {
              layout: {
                buttonText: {
                  next: '다음',
                  back: '이전',
                  submit: '제출',
                  goHome: '홈으로',
                },
              },
              intro: {
                title: '코칭 온보딩 서베이',
                description:
                  '나다운 리더십을 <br /> 찾아가는 여정 <br /> 시작해 볼까요?',
                questions: {
                  title: '아래 항목을 질문 드려요',
                  current_concerns: '현재 직면한 고민은 무엇인가요?',
                  expected_changes: '어떤 변화를 기대하고 오셨나요?',
                  coaching_style: '어떤 방식의 코칭을 선호하시나요?',
                  estimated_time: '소요시간 약 5분',
                },
                start_button: '시작하기',
              },
              page1: {
                title: '원하시는 코칭 주제를 <br /> 최대 2개 선택해 주세요',
                topics: {
                  self_understanding: {
                    title: '자기 이해',
                    description:
                      '나의 성향과 강점을 알고 그에 맞는 리더십을 구축하고 싶습니다.',
                  },
                  emotional_control: {
                    title: '감정 조절',
                    description:
                      '감정이 태도가 되지 않도록, 평정심을 지키고 싶습니다.',
                  },
                  stress_management: {
                    title: '번아웃 · 스트레스 관리',
                    description:
                      '에너지 방전을 예방하고 소진된 에너지를 충전하고 싶습니다.',
                  },
                  communication: {
                    title: '소통 · 피드백',
                    description:
                      '구성원과 어떻게 소통하고 피드백 해야 할 지 모르겠어요.',
                  },
                  growth_support: {
                    title: '팀 성장 · 동기부여',
                    description:
                      '구성원과 조직의 니즈의 접점을 찾아 동기부여하며 업적 성장을 이끌고 싶습니다.',
                  },
                  hiring_management: {
                    title: '위임 · 성과 관리',
                    description:
                      '마이크로매니징 대신 적절한 위임을 통해 성과를 창출하고 싶습니다.',
                  },
                  relationship: {
                    title: '관계 갈등',
                    description:
                      '특정 팀원/동료와 반복되는 갈등을 해소하고 싶습니다.',
                  },
                  goal_achievement: {
                    title: '목표 · 전략 수립',
                    description:
                      '팀의 비전, 목표와 그를 달성하기 위한 전략을 정리하여 소통하고 싶습니다.',
                  },
                  culture_change: {
                    title: '조직 문화 · 변화 관리',
                    description:
                      '큰 변화의 국면에서 이에 맞는 새로운 조직 문화를 구축/전파하고 구성원들이 적응하도록 이끌고 싶습니다.',
                  },
                },
                errorMessage:
                  '최대 2개까지 선택 가능합니다.\n이미 선택한 항목을 취소 후 선택해 주세요.',
              },
              page2: {
                title: '코칭을 통해 달성하고 싶은 <br /> 목표는 무엇인가요?',
                selectedTopics: '앞서 선택한 고민',
                topicLabels: {
                  self_understanding: '자기 이해',
                  emotional_control: '감정 조절',
                  stress_management: '번아웃 · 스트레스 관리',
                  communication: '소통 · 피드백',
                  growth_support: '팀 성장 · 동기부여',
                  hiring_management: '위임 · 성과 관리',
                  relationship: '관계 갈등',
                  goal_achievement: '목표 · 전략 수립',
                  culture_change: '조직 문화 · 변화 관리',
                },
                goalDescription: '코칭 후 기대하는 모습을 적어주세요',
                goalDescriptionPlaceholder:
                  '코칭 이후, 나는 어떻게 느끼고 생각하며, 어떤 리더십을 발휘하게 될까요? 변화된 나의 모습을 생생하게 그려보세요. 선명하게 그릴수록, 그 변화는 더 가까워집니다.',
                goalBackground: '목표를 정하게 된 배경을 적어주세요.',
                goalBackgroundPlaceholder:
                  '현재 극복하고 싶은 어려움이나 더 나아지고 싶은 부분을 떠올려 보세요. 특별히 기억나는 상황이 있다면 적어주셔도 좋습니다.',
                maxLength: '최대 200자',
              },
              page3: {
                title:
                  '작성해 주신 목표에 <br /> 현재 얼마나 가까운 것 같나요?',
                description:
                  '슬라이더를 움직여 현재 목표에 대한 확신도를 선택해주세요.',
                description2:
                  '지금 확신이 부족해도 괜찮습니다. 코칭을 통해 원하는 모습에 가까워질 수 있도록 함께하겠습니다.',
              },
              coachIntro: {
                title: '업피플 리더십 코칭',
                description:
                  '코칭이란, 스스로의<br />잠재력을 발견하고<br /><span>나와 팀의 성장</span>을<br /><span>가속화하는 여정</span>입니다',
                quote: {
                  description:
                    '스스로 답을 찾도록 <br /> 최적의 질문들을 던져주셔서, <br />매 세션마다 결론을 얻을 수<br /> 있었습니다.',
                  by: '메디컬 AI 상장사 팀장',
                },
              },
              part1: {
                title: '코칭 온보딩 서베이 <span>Part 1.</span>',
                description:
                  '본 응답 결과는 향후 코칭 방향성 설정 및 적합한 코치 매칭을 위한 참고자료로 활용되며, 외부에 공유되지 않습니다. 느끼는 대로 솔직하게 응답해 주세요.',
                questions: {
                  coaching_confidence:
                    '나는 코칭이 나의 성장에 <br /> 큰 도움이 될 것이라 확신한다.',
                  topic_clarity:
                    '나는 코칭을 통해 다루고자 <br /> 하는 주제가 뚜렷하다.',
                  timing_relevance:
                    '지금은 내가 코칭을 받기 <br /> 적절한 타이밍이다.',
                  previous_coaching_experience:
                    '이전에 전문적인 1:1 코칭을 <br /> 받아본 적 있다.',
                },
                answers: {
                  '1': '전혀 아니다',
                  '2': '아니다',
                  '3': '보통이다',
                  '4': '그렇다',
                  '5': '매우 그렇다',
                  previous_coaching_experience_yes: '경험이 있어요',
                  previous_coaching_experience_no: '1:1 코칭은 처음이에요',
                },
              },
              coachMatching: {
                title: '코칭 온보딩 서베이',
                description:
                  '<h2>지금 묻는 설문을 바탕으로 </h2><h1>최적의 코치를 매칭하고 <br /> 앞으로의 코칭 여정을 <br /> 설계해 드려요 </h1>',
              },
              part2: {
                title: '코칭 온보딩 서베이 <span>Part 2.</span>',
                description:
                  '본 응답 결과는 향후 코칭 방향성 설정 및 적합한 코치 매칭을 위한 참고자료로 활용되며, 외부에 공유되지 않습니다. 느끼는 대로 솔직하게 응답해 주세요.',
                preferredGuidanceStyle: {
                  question: '코칭 과정에서 기대하는 것은',
                  options: {
                    a: '스스로 방향 정립',
                    b: '코치의 적극적 가이드',
                  },
                },
                preferredProblemSolvingApproach: {
                  question: '코치에게 기대하는 바는',
                  options: {
                    a: '문제의 근원 파악',
                    b: '빠른 액션 도출',
                  },
                },
                preferredFeedbackStyle: {
                  question: '선호하는 피드백 방식은',
                  options: {
                    a: '완곡, 부드러운 소통',
                    b: '직설적, 명확한 소통',
                  },
                },
                preferredCoachExpertise: {
                  question: '코치에게 기대하는 점은',
                  options: {
                    a: '사람에 대한 깊은 통찰',
                    b: '실무 지식과 경험',
                  },
                },
                coachRequests: {
                  question:
                    '이외에 코치에게 바라는 점이 <br /> 있다면 무엇인가요?',
                  placeholder: '요청사항을 입력해 주세요.',
                  maxLength: '최대 150자',
                },
                confirmModal: {
                  title: '결과를 최종 제출하시겠습니까?',
                  cancelButtonText: '아니오',
                  confirmButtonText: '네',
                },
                successModal: {
                  title: '결과가 제출되었습니다!',
                  confirmButtonText: '확인',
                },
              },
              result: {
                title: '세상의 모든 <br/> 리더는 특별합니다',
                description:
                  '<p>업피플과 함께,</p><p><span>내 안의 잠재력</span>을</p><p>발견해 보세요</p>',
                nextButtonText: '홈으로 가기',
              },
            },
          },
          co: {
            coachingManagement: {
              confirm: '확인',
              meetingUrlMissing:
                '미팅 링크를 찾을 수 없습니다. 잠시 후 다시 시도해주세요.',
              accountNotice: {
                title: '계정 확인 안내',
                body: '구글 밋(Google Meet) 접속 시, 브라우저에서 업피플 도메인 계정(up-people.com)이 아닌 다른 계정으로 로그인되어 있을 경우 코칭 세션 입장이 제한될 수 있습니다.',
                highlight:
                  '원활한 코칭 세션 진행을 위해 반드시 업피플 도메인 계정(up-people.com)으로 로그인(계정 전환)하신 후 접속해 주시기 바랍니다.',
                confirmButton: '내용을 확인했습니다',
              },
            },
          },
        },
      },
    },
    detection: {
      order: ['navigator'],
    },
    supportedLngs: ['en', 'ko'],
    fallbackLng: 'ko',
    interpolation: {
      escapeValue: false,
      format: (value, format) => {
        if (format === 'ordinal') {
          const num = Number(value);
          if (Number.isNaN(num)) return value;
          return getOrdinal(num);
        }
        return value;
      },
    },
  });

export type SupportedLanguage = 'en' | 'ko';

const isSupportedLanguage = (
  language: unknown,
): language is SupportedLanguage => {
  return language === 'en' || language === 'ko';
};

// 언어 변경 함수
export const changeLanguage = (language: unknown) => {
  let safeLang: SupportedLanguage;
  if (isSupportedLanguage(language)) {
    safeLang = language;
  } else {
    console.warn(
      `Unsupported language: ${language}, using default language 'ko'`,
    );
    safeLang = 'ko';
  }
  i18n.changeLanguage(safeLang);
};

export default i18n;
